/**
 * Case files — the wire shape, and every coercion applied to it (Build 8).
 *
 * WHY THIS IS A SEPARATE MODULE FROM src/lib/api.ts.
 *
 * Two reasons, and the second is the one that decided it.
 *
 * 1. THIS IS WHERE THE RISK IS. The fetch wrappers in api.ts are a thin repeat
 *    of a pattern already proven three times over (questions, leads, billing):
 *    send a Bearer token, read JSON, throw a typed error on a non-2xx. The
 *    coercion below is different. It is the last thing between the engine's
 *    JSON and what an owner reads as a record of their business, and it has one
 *    property that would fail silently — see `toDetail`.
 *
 * 2. IT MUST BE TESTABLE WITHOUT WIDENING A PRODUCTION GATE. tests/ belongs to
 *    tsconfig.worker.json, whose `types` are deliberately limited to the
 *    Workers runtime so that a worker file reaching for a Node or DOM global
 *    fails at the gate rather than at the edge. src/lib/api.ts imports
 *    src/lib/env.ts, which reads `import.meta.env` — a Vite type. Importing
 *    api.ts from a test would mean adding `vite/client` to that project and
 *    declaring DOM globals across the whole engine, buying test coverage by
 *    weakening a gate that exists to catch a worse class of bug.
 *
 *    This module imports NOTHING. It is pure functions over `unknown`, so it
 *    typechecks and runs under the Workers project as-is.
 *
 * NOTHING OFF THE WIRE IS TRUSTED. Same rule as `toLead` in api.ts: a malformed
 * row must never reach a control as an invalid value.
 */

export type CaseFileStatus = 'open' | 'closed'
export type CaseFileOutcome = 'quoted' | 'won' | 'declined' | 'no_action'
export type TouchSource = 'lead' | 'missed_call' | 'whatsapp_message' | 'follow_up'

export interface CaseFileContact {
  name: string | null
  phone: string | null
}

export type TouchDetail =
  | {
      kind: 'lead'
      customerName: string
      customerPhone: string
      jobType: string
      propertyType: string
      area: string
      estimatedValue: string
      estimatedDuration: string
      callbackTime: string
      leadScore: string
      status: string
    }
  | { kind: 'missed_call'; outcome: string; settledAt: number | null }
  | { kind: 'whatsapp_message'; outcome: string; settledAt: number | null }
  | {
      kind: 'follow_up'
      type: string
      status: string
      scheduledFor: number
      sentAt: number | null
    }

export interface CaseFileTouch {
  id: string
  source: TouchSource
  sourceId: string
  occurredAt: number
  attachedAt: number
  /** null when the source row has been ERASED. Not the same as "no detail". */
  detail: TouchDetail | null
}

export interface CaseFileSummary {
  id: string
  status: CaseFileStatus
  outcome: CaseFileOutcome | null
  openedAt: number
  closedAt: number | null
  updatedAt: number
  touchCount: number
  lastTouchAt: number | null
  channels: TouchSource[]
  contact: CaseFileContact | null
}

export interface CaseFileDetail extends CaseFileSummary {
  touches: CaseFileTouch[]
  truncated: boolean
}

export const TOUCH_SOURCES = ['lead', 'missed_call', 'whatsapp_message', 'follow_up'] as const
export const CASE_FILE_OUTCOMES = ['quoted', 'won', 'declined', 'no_action'] as const

/** Human wording for each outcome, used by the dashboard and by nothing else. */
export const OUTCOME_LABELS: Record<CaseFileOutcome, string> = {
  quoted: 'Quoted',
  won: 'Won',
  declined: 'Declined',
  no_action: 'No action',
}

/** Human wording for each channel. */
export const SOURCE_LABELS: Record<TouchSource, string> = {
  lead: 'Chat enquiry',
  missed_call: 'Missed call',
  whatsapp_message: 'WhatsApp',
  follow_up: 'Follow-up sent',
}

function asString(v: unknown): string {
  return typeof v === 'string' ? v : ''
}
function asNumber(v: unknown): number {
  return typeof v === 'number' ? v : 0
}
function asNumberOrNull(v: unknown): number | null {
  return typeof v === 'number' ? v : null
}

/** An outcome, or null if it is not one this engine knows. */
export function asOutcome(v: unknown): CaseFileOutcome | null {
  return (CASE_FILE_OUTCOMES as readonly unknown[]).includes(v) ? (v as CaseFileOutcome) : null
}

/**
 * The already-closed outcome carried on the close endpoint's 409.
 *
 * Separated out because a 409 is NOT a failure — it is almost always a stale
 * tab, and the difference between telling an owner "already closed as quoted"
 * and telling them something went wrong is the difference between a shrug and a
 * support call about data loss.
 */
export function parseAlreadyClosedOutcome(body: unknown): CaseFileOutcome | undefined {
  if (body === null || typeof body !== 'object') return undefined
  return asOutcome((body as Record<string, unknown>).outcome) ?? undefined
}

/** '' from the engine means absent; both empty means no contact held at all. */
export function toContact(raw: unknown): CaseFileContact | null {
  if (raw === null || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const name = typeof r.name === 'string' && r.name.trim() !== '' ? r.name : null
  const phone = typeof r.phone === 'string' && r.phone.trim() !== '' ? r.phone : null
  if (name === null && phone === null) return null
  return { name, phone }
}

/** Unrecognised channels are dropped rather than rendered as unknown chips. */
export function toChannels(raw: unknown): TouchSource[] {
  if (!Array.isArray(raw)) return []
  return raw.filter((s): s is TouchSource => (TOUCH_SOURCES as readonly unknown[]).includes(s))
}

export function toSummary(raw: unknown): CaseFileSummary {
  const r = (raw ?? {}) as Record<string, unknown>
  return {
    id: asString(r.id),
    // Anything unrecognised reads as OPEN, and the asymmetry is deliberate: a
    // live file wrongly shown as closed hides work the owner should be acting
    // on, while a closed one wrongly shown as open is merely noise — and the
    // engine refuses the close anyway.
    status: r.status === 'closed' ? 'closed' : 'open',
    outcome: asOutcome(r.outcome),
    openedAt: asNumber(r.openedAt),
    closedAt: asNumberOrNull(r.closedAt),
    updatedAt: asNumber(r.updatedAt),
    touchCount: asNumber(r.touchCount),
    lastTouchAt: asNumberOrNull(r.lastTouchAt),
    channels: toChannels(r.channels),
    contact: toContact(r.contact),
  }
}

/**
 * Coerce one touch's detail.
 *
 * THE PROPERTY THIS MODULE EXISTS TO PROTECT. Returns null for anything not
 * recognised — which is the SAME value the engine sends for a touch whose
 * source row has been ERASED, and that is deliberate. Both mean "this enquiry
 * happened and there is no detail to show for it".
 *
 * The tempting alternative is to substitute an empty object so the renderer
 * always has something. That would make an erasure indistinguishable from a
 * touch that simply had no fields, on a screen the owner reads as the record of
 * their business — and nothing downstream could detect it, because an empty
 * object renders as plausibly as a real one.
 */
export function toDetail(raw: unknown): TouchDetail | null {
  if (raw === null || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  switch (r.kind) {
    case 'lead':
      return {
        kind: 'lead',
        customerName: asString(r.customerName),
        customerPhone: asString(r.customerPhone),
        jobType: asString(r.jobType),
        propertyType: asString(r.propertyType),
        area: asString(r.area),
        estimatedValue: asString(r.estimatedValue),
        estimatedDuration: asString(r.estimatedDuration),
        callbackTime: asString(r.callbackTime),
        leadScore: asString(r.leadScore),
        status: asString(r.status),
      }
    case 'missed_call':
    case 'whatsapp_message':
      return {
        kind: r.kind,
        outcome: asString(r.outcome),
        settledAt: asNumberOrNull(r.settledAt),
      }
    case 'follow_up':
      return {
        kind: 'follow_up',
        type: asString(r.type),
        status: asString(r.status),
        scheduledFor: asNumber(r.scheduledFor),
        sentAt: asNumberOrNull(r.sentAt),
      }
    default:
      return null
  }
}

/**
 * Coerce one touch, or null if it cannot be placed on a timeline at all.
 *
 * A touch with no recognisable SOURCE is the only thing this module ever drops
 * — it has no channel to sit under and no label to render. It cannot occur
 * against this engine, whose schema CHECKs the vocabulary; dropping it is the
 * behaviour for a wire that is not this engine.
 */
export function toTouch(raw: unknown): CaseFileTouch | null {
  const r = (raw ?? {}) as Record<string, unknown>
  if (!(TOUCH_SOURCES as readonly unknown[]).includes(r.source)) return null
  return {
    id: asString(r.id),
    source: r.source as TouchSource,
    sourceId: asString(r.sourceId),
    occurredAt: asNumber(r.occurredAt),
    attachedAt: asNumber(r.attachedAt),
    detail: toDetail(r.detail),
  }
}

/**
 * Parse the list response, or null if it is not the shape we expect.
 *
 * Returns null rather than throwing so this module stays dependency-free — the
 * caller in api.ts owns the error type, because that is where the rest of the
 * client's errors are already defined.
 */
export function parseCaseFileList(
  data: unknown,
): { caseFiles: CaseFileSummary[]; truncated: boolean } | null {
  if (data === null || typeof data !== 'object') return null
  const body = data as Record<string, unknown>
  if (!Array.isArray(body.caseFiles)) return null
  return {
    caseFiles: body.caseFiles.map(toSummary),
    // Absent reads as "not truncated". The flag only ever ADDS a warning, so a
    // missing one shows a complete list rather than an invented caveat.
    truncated: body.truncated === true,
  }
}

/** Parse the detail response, or null if it is not the shape we expect. */
export function parseCaseFileDetail(data: unknown): CaseFileDetail | null {
  if (data === null || typeof data !== 'object') return null
  const raw = (data as Record<string, unknown>).caseFile
  if (raw === null || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const touches = Array.isArray(r.touches)
    ? r.touches.map(toTouch).filter((t): t is CaseFileTouch => t !== null)
    : []
  return { ...toSummary(raw), touches, truncated: r.truncated === true }
}
